import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useEffect,
    useCallback,
} from 'react'
import { UserTag } from '../models/Users/types'
import { axiosInstance } from '../helpers'
import { IdentifiableObject } from '../models'
import { useUser } from '../hooks'

interface UserTagsContextType {
    userTags: UserTag[]
    getUserTags: () => Promise<UserTag[]>
    getGlobalTags: (organizationId?: number) => Promise<UserTag[]>
    copyGlobalTag: (tagId: number, organizationId?: number) => Promise<UserTag>
    createUserTag: (request: CreateUserTagRequest) => Promise<UserTag>
    updateUserTag: (request: UpdateUserTagRequest) => Promise<UserTag>
    deleteUserTag: (id: number) => Promise<void>
    loading: boolean
}

const UserTagsContext = createContext<UserTagsContextType | undefined>(
    undefined,
)

interface UserTagsProviderProps {
    children: ReactNode
}

export const UserTagsProvider: React.FC<UserTagsProviderProps> = ({
    children,
}) => {
    const { organization } = useUser()

    const [userTags, setUserTags] = useState<UserTag[]>([])
    const [loading, setLoading] = useState(false)

    const getUserTags = useCallback(async () => {
        setLoading(true)
        try {
            const res = await axiosInstance.get('user/user_tags/')
            const userTags: UserTag[] = res.data
            setUserTags(res.data)
            return userTags
        } catch (error) {
            console.error(error)
            return Promise.reject(error)
        } finally {
            setLoading(false)
        }
    }, [])

    const getGlobalTags = useCallback(async (organizationId?: number) => {
        const params =
            organizationId != null ? { organization_id: organizationId } : {}
        const res = await axiosInstance.get<UserTag[]>(
            'user/user_tags/global_tags/',
            { params },
        )
        return res.data
    }, [])

    const copyGlobalTag = useCallback(
        async (tagId: number, organizationId?: number) => {
            const params =
                organizationId != null
                    ? { organization_id: organizationId }
                    : {}
            const res = await axiosInstance.post<UserTag>(
                'user/user_tags/copy_global_tag/',
                { tag_id: tagId },
                { params },
            )
            const newTag: UserTag = res.data
            setUserTags((prev) => [...prev, newTag])
            return newTag
        },
        [],
    )

    // Auto-fetch tags when provider mounts
    useEffect(() => {
        getUserTags()
    }, [organization])

    const createUserTag = async (request: CreateUserTagRequest) => {
        setLoading(true)
        try {
            const res = await axiosInstance.post('user/user_tags/', request)
            const userTag: UserTag = res.data
            setUserTags([...userTags, userTag])
            return userTag
        } catch (error) {
            console.error(error)
            return Promise.reject(error)
        } finally {
            setLoading(false)
        }
    }

    const updateUserTag = async (request: UpdateUserTagRequest) => {
        setLoading(true)
        try {
            const res = await axiosInstance.patch(
                `user/user_tags/${request.id}/`,
                request,
            )
            const userTag: UserTag = res.data
            _insertOrUpdateIdentifiable(userTag, setUserTags)
            return userTag
        } catch (error) {
            console.error(error)
            return Promise.reject(error)
        } finally {
            setLoading(false)
        }
    }

    const deleteUserTag = async (id: number) => {
        setLoading(true)
        try {
            await axiosInstance.delete(`user/user_tags/${id}/`)
            setUserTags(userTags.filter((tag) => tag.id !== id))
        } catch (error) {
            console.error(error)
            return Promise.reject(error)
        } finally {
            setLoading(false)
        }
    }

    const _insertOrUpdateIdentifiable = <T extends IdentifiableObject>(
        updatedValue: T,
        dispatch: React.Dispatch<React.SetStateAction<T[]>>,
    ) => {
        dispatch((old) => {
            const safeOldValue = old ? [...old] : []

            let found = false
            for (let i = 0; i < safeOldValue.length; i++) {
                const current = safeOldValue[i]
                if (current.id === updatedValue.id) {
                    safeOldValue[i] = updatedValue
                    found = true
                    break
                }
            }

            if (!found) {
                safeOldValue.push(updatedValue)
            }

            return safeOldValue
        })
    }

    const value: UserTagsContextType = {
        userTags,
        getUserTags,
        getGlobalTags,
        copyGlobalTag,
        createUserTag,
        updateUserTag,
        deleteUserTag,
        loading,
    }

    return (
        <UserTagsContext.Provider value={value}>
            {children}
        </UserTagsContext.Provider>
    )
}

export const useUserTags = (): UserTagsContextType => {
    const context = useContext(UserTagsContext)
    if (context === undefined) {
        throw new Error('useUserTags must be used within a UserTagsProvider')
    }
    return context
}

export interface CreateUserTagRequest {
    name: string
    color: string
    apartment_ids: number[]
    is_global_tag?: boolean
}

export interface UpdateUserTagRequest {
    id: number
    name?: string
    color?: string
    apartment_ids?: number[]
    is_global_tag?: boolean
}
